import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, User, Phone, Mail, QrCode, Package, LogOut, ChevronRight } from "lucide-react";
import api from "../api/axios";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import BrandLogo from "../components/shared/BrandLogo";
import LanguageSwitcher from "../components/shared/LanguageSwitcher";

const STATUS_COLORS = {
  Received: "bg-blue-100 text-blue-700",
  "In Progress": "bg-amber-100 text-amber-700",
  Shipped: "bg-purple-100 text-purple-700",
  Delivered: "bg-green-100 text-green-700",
  Cancelled: "bg-red-100 text-red-700",
};

const ProfilePage = () => {
  const navigate = useNavigate();
  const { token, logout } = useAuth();
  useLanguage(); // context subscription

  const [profile, setProfile] = useState(null);
  const [locketId, setLocketId] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Redirect if not authenticated
  useEffect(() => {
    if (!token) {
      navigate("/");
    }
  }, [token, navigate]);

  // Fetch user data
  useEffect(() => {
    if (!token) return;

    const fetchProfile = async () => {
      try {
        const res = await api.get("/qr/me", {
          headers: { Authorization: `Bearer ${token}` },
        });
        setProfile(res.data.profile);
        setLocketId(res.data.locketId || null);
        setOrders(res.data.orders || []);
      } catch (err) {
        const msg = err.response?.data?.error || "Failed to load profile";
        setError(msg);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [token]);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return "—";
      return d.toLocaleDateString("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    } catch {
      return "—";
    }
  };

  if (!token) return null;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <nav className="bg-white border-b border-slate-200 shadow-sm">
          <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate("/")}
              className="p-2 rounded-lg text-slate-600 hover:bg-slate-100 transition-colors duration-150"
              aria-label="Go back"
            >
              <ArrowLeft size={20} />
            </button>
            <BrandLogo />
          </div>
        </nav>
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="w-10 h-10 border-4 border-red-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-slate-600 font-medium text-sm">Loading profile...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50">
        <nav className="bg-white border-b border-slate-200 shadow-sm">
          <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate("/")}
              className="p-2 rounded-lg text-slate-600 hover:bg-slate-100 transition-colors duration-150"
              aria-label="Go back"
            >
              <ArrowLeft size={20} />
            </button>
            <BrandLogo />
          </div>
        </nav>
        <div className="max-w-2xl mx-auto px-4 py-10">
          <div className="bg-white rounded-lg shadow-md border-2 border-red-200 p-8 text-center">
            <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <User size={28} className="text-red-600" />
            </div>
            <h2 className="text-xl font-bold text-red-700 mb-2">Could not load profile</h2>
            <p className="text-slate-600 text-sm mb-6">{error}</p>
            <button
              onClick={() => navigate("/")}
              className="bg-red-600 hover:bg-red-700 text-white font-bold text-sm px-6 py-3 rounded-lg transition-colors duration-150"
            >
              Go Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navigation bar */}
      <nav className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-40">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate("/")}
              className="p-2 rounded-lg text-slate-600 hover:bg-slate-100 transition-colors duration-150"
              aria-label="Go back"
            >
              <ArrowLeft size={20} />
            </button>
            <BrandLogo />
          </div>
        </div>
      </nav>

      {/* Main content */}
      <div className="max-w-2xl mx-auto px-4 py-6 space-y-4">
        {/* User Info Card */}
        <div className="bg-white rounded-lg shadow-md border border-slate-200 p-5">
          <div className="flex items-center gap-4 mb-4">
            {profile?.photo ? (
              <img
                src={profile.photo}
                alt={profile.name || "Profile"}
                className="w-16 h-16 rounded-lg object-cover border-2 border-slate-200"
              />
            ) : (
              <div className="w-16 h-16 rounded-lg bg-red-100 flex items-center justify-center border-2 border-red-200">
                <span className="text-2xl font-bold text-red-600">
                  {profile?.name ? profile.name.charAt(0).toUpperCase() : "?"}
                </span>
              </div>
            )}
            <div>
              <h2 className="text-lg font-bold text-slate-900">{profile?.name || "User"}</h2>
              <p className="text-xs text-slate-500">Emergency QR Account</p>
            </div>
          </div>

          <div className="space-y-3">
            {profile?.phone && (
              <div className="flex items-center gap-3 text-sm">
                <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                  <Phone size={16} className="text-slate-600" />
                </div>
                <div>
                  <p className="text-[11px] text-slate-400 font-medium">Phone</p>
                  <p className="text-slate-800 font-medium">{profile.phone}</p>
                </div>
              </div>
            )}
            {profile?.email && (
              <div className="flex items-center gap-3 text-sm">
                <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                  <Mail size={16} className="text-slate-600" />
                </div>
                <div>
                  <p className="text-[11px] text-slate-400 font-medium">Email</p>
                  <p className="text-slate-800 font-medium">{profile.email}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Linked QR Codes */}
        <div className="bg-white rounded-lg shadow-md border border-slate-200 p-5">
          <h3 className="text-sm font-bold text-slate-900 mb-3 flex items-center gap-2">
            <QrCode size={16} className="text-red-600" />
            Linked QR Codes
          </h3>
          {locketId ? (
            <Link
              to={`/${locketId}/info`}
              className="flex items-center justify-between p-3 rounded-lg border border-slate-200 hover:border-red-300 hover:bg-red-50/30 transition-colors duration-150 no-underline"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                  <QrCode size={20} className="text-red-600" />
                </div>
                <div>
                  <p className="font-mono font-bold text-slate-900 text-sm">{locketId}</p>
                  <p className="text-[11px] text-slate-500">Tap to edit emergency info</p>
                </div>
              </div>
              <ChevronRight size={18} className="text-slate-400" />
            </Link>
          ) : (
            <div className="text-center py-4">
              <p className="text-slate-500 text-sm mb-2">No QR code linked yet</p>
              <p className="text-xs text-slate-400">
                Set up a QR code sticker to link it to your account
              </p>
            </div>
          )}
        </div>

        {/* Order History */}
        <div className="bg-white rounded-lg shadow-md border border-slate-200 p-5">
          <h3 className="text-sm font-bold text-slate-900 mb-3 flex items-center gap-2">
            <Package size={16} className="text-red-600" />
            Order History
          </h3>
          {orders.length > 0 ? (
            <div className="space-y-2">
              {orders.map((order) => (
                <Link
                  key={order._id}
                  to={`/order/${order._id}/track`}
                  className="flex items-center justify-between p-3 rounded-lg border border-slate-200 hover:border-red-300 hover:bg-red-50/30 transition-colors duration-150 no-underline"
                >
                  <div>
                    <div className="flex items-center gap-2 mb-0.5">
                      <p className="font-semibold text-slate-900 text-sm">
                        {order.planType === "family" ? "Family Pack" : "Individual"}
                      </p>
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-lg ${STATUS_COLORS[order.status] || "bg-slate-100 text-slate-600"}`}>
                        {order.status}
                      </span>
                    </div>
                    <p className="text-[11px] text-slate-500">
                      {formatDate(order.createdAt)} &middot; {order.paymentMethod === "cod" ? "COD" : "Paid Online"}
                    </p>
                  </div>
                  <ChevronRight size={18} className="text-slate-400 flex-shrink-0" />
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-4">
              <p className="text-slate-500 text-sm mb-2">No orders yet</p>
              <Link
                to="/order"
                className="text-red-600 text-sm font-semibold hover:text-red-700 transition-colors duration-150"
              >
                Order your first QR code sticker
              </Link>
            </div>
          )}
        </div>

        {/* Logout Button */}
        <button
          onClick={handleLogout}
          className="w-full flex items-center justify-center gap-2 bg-white border-2 border-red-200 text-red-600 font-semibold py-3 rounded-lg hover:bg-red-50 transition-colors duration-150"
        >
          <LogOut size={18} />
          Logout
        </button>
      </div>

      {/* Language Switcher - fixed bottom-right */}
      <div className="fixed bottom-6 right-6 z-50 flex flex-col items-center gap-1">
        <LanguageSwitcher />
      </div>
    </div>
  );
};

export default ProfilePage;
